import { useContext, useEffect, useMemo, useState } from 'react';
import { useToastError } from '~/utils/useToastError';
import { useFormik } from 'formik';
import { validate } from '~/utils/validateCreateFood';
import { Button, InputBar, Textarea, Badge, IconButton } from '~/ui/index';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { IAsset } from '../AccommodationForm';
import styles from './FoodForm.module.scss';
import { instance } from '~/utils/api/api';
import { AuthenticationContext } from '../Authentication/AuthenticationProvider';
import {
  checkIfUserAssetAdmin,
  checkIfUserSuperAdmin,
} from '~/utils/getUserRole';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import CustomSelect from '../Shared/CustomSelect/CustomSelect';
import { getTimezones } from '../UserForm/timezones';
import { FolderSelect } from '../Shared/FolderSelect/FolderSelect';
import { Modal } from '../Shared/Modal/Modal';
import PhotoPickerContainer, {
  IPhoto,
} from '../AnnouncementForm/PhotoPickerContainer/PhotoPickerContainer';
import PhotoIcon from '~/assets/svg/newSvg/picture.svg?react';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import { Checkbox, DatePicker } from 'antd';
import { useDebounce } from '@uidotdev/usehooks';
import { getFoodPhotos } from '~/utils/api/queries';
import dayjs, { Dayjs } from 'dayjs';
import { getDays } from '~/utils/getDays';

interface IFolder {
  id: number;
  name: string;
  child_folders: IFolder[];
}

interface IBoxOffice {
  id: number;
  name: string;
}

export interface FoodFormValues {
  id?: number | null;
  name: string;
  description: string;
  address: string;
  is_active: null | {
    id: number;
    name: JSX.Element;
  };
  photos: IPhoto[];
  foodTypes?: any;
  pointType: null | number;
  pointObject: { id: number; name: string };
  food_accommodation: FoodAccommodation[];
  isAllAccomodation: boolean;
  assets: { id: number; name: string; box_office: IBoxOffice[] };
  timezone: {
    id: string;
    name: string;
  };
  box_office: IBoxOffice;
  price_list_code: { id: number; name: string };
  payment_type: { id: number; name: string }[];
  available_kso_order: { id: number; name: string };
  containers_enabled: { id: number; name: string };
  cooking_time: string;
  limit_hour: Dayjs | null;
  folder: IFolder;
  popup_text: string;
  popup_is_active: boolean;
  enabled_scanner_barcode: { id: number; name: string };
  days: { id: number; name: string; displayName: string; type: string }[];
  working_days_all: boolean;
  food_types: { id: number; name: string; type: string }[];
  available_order_today: { id: number; name: string };
}

interface FoodAccommodation {
  id: number;
  name: string;
  full_name: string;
}

export interface FoodItem {
  accommodation_id?: null;
  address: string;
  description: string;
  name: string;
  food_menu_id?: number;
  is_active: null | {
    id: number;
    name: JSX.Element;
  };
  food_accommodation: FoodAccommodation[];
  food_types?: {
    id: number;
    end_time: string;
    start_time: string;
    repeat: string;
    name: string;
    display_name: string;
    type: string;
    foodTime?: {
      name: string;
      start_time: string;
      end_time: string;
    };
  }[];
  food_object_type: { id: number; name: string };
  object_type_id: number;
  photos: IPhoto[];
  pointType: number;
  assets: IAsset;
  timezone: { id: string; name: string };
  active_autosend: boolean;
  box_office: IBoxOffice;
  payment_types: { id: number; name: string; type: string }[];
  price_list_code: string | null;
  containers_enabled: boolean;
  cooking_time: number | null;
  folder: IFolder | null;
  popup_text: string;
  popup_is_active: boolean;
  limit_hour: Dayjs | null;
  available_kso_order: boolean;
  enabled_scanner_barcode: boolean;
  available_day_of_week: [];
  available_order_today: boolean;
}

interface FoodFormProps {
  foodItem?: FoodItem;
  onSubmit: (values: FoodFormValues) => void;
  storageKey?: string;
  isAssetEditAvailable?: boolean;
  isLoading?: boolean;
  hasAvailable?: boolean;
}

const setDays = (
  days: string[],
  options: { id: number; name: string; type: string; displayName: string }[]
) => {
  if (days.length === 0) return [];

  const filteredDays = options.filter(item => days.includes(item.type));
  return filteredDays;
};

const getStatusOptions = () => {
  return [
    {
      id: 1,
      name: (
        <Badge className={styles.statusBadge} variant="success">
          {t('order_available')}
        </Badge>
      ),
    },
    {
      id: 0,
      name: (
        <Badge className={styles.statusBadge} variant="error">
          {t('order_not_available')}
        </Badge>
      ),
    },
  ];
};

export const FoodForm = ({
  isLoading,
  foodItem,
  onSubmit,
  storageKey,
  isAssetEditAvailable,
  hasAvailable = false,
}: FoodFormProps): JSX.Element => {
  const { user } = useContext(AuthenticationContext);
  const isHaveNotEnoughRole = !checkIfUserSuperAdmin(user?.data.role_name);
  const isAssetAdmin = !checkIfUserAssetAdmin(user?.data.role_name);
  const toastError = useToastError();
  const [assets, setAssets] = useState<IAsset[]>([]);
  const { t } = useTranslation();
  const [counter, setCounter] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const days = getDays();

  const timezonesList = getTimezones();

  const barcodeOptions = useMemo(
    () => [
      { id: 0, name: t('no') },
      { id: 1, name: t('yes') },
    ],
    [t]
  );

  const loadAssets = async () => {
    try {
      const { data } = await instance.get('admin/assets', {
        params: { page: 1, perPage: 100 },
      });
      setAssets(data.data);
    } catch {
      toastError(`${t('failed_to_load')} ${t('assets')}`);
    }
  };

  const containerOptions = [
    { id: 0, name: t('not_available_containers') },
    { id: 1, name: t('available') },
  ];

  const { data: pointTypes } = useQuery({
    queryFn: async () => {
      const data = await instance.get('food-admin-object-types');
      return data;
    },
    queryKey: ['food-point-types'],
  });

  const { data: foodCategory, isLoading: isFoodTypeLoading } = useQuery({
    queryFn: async () => {
      const data = await instance.get('admin/food-types');
      return data?.data;
    },
    queryKey: ['food-type'],
  });

  const { initialValues, storedInitialValues, clear, create } =
    useLocalStorage<FoodFormValues>({
      initialValues: {
        name: '',
        description: '',
        address: '',
        popup_text: '',
        popup_is_active: false,
        is_active: null,
        pointType: null,
        pointObject: {
          id: 0,
          name: '',
        },
        assets: {
          id: 0,
          name: '',
          box_office: [],
        },
        timezone: {
          id: '',
          name: '',
        },
        photos: [],
        food_accommodation: [],
        isAllAccomodation: false,
        box_office: { id: 0, name: '' },
        price_list_code: { id: 0, name: '' },
        payment_type: [],
        available_kso_order: { id: 0, name: t('no') },
        containers_enabled: { id: 0, name: t('not_available_containers') },
        cooking_time: '',
        limit_hour: null,
        folder: { id: 0, name: '', child_folders: [] },
        enabled_scanner_barcode: { id: 0, name: t('no') },
        days: [],
        working_days_all: false,
        food_types: [],
        available_order_today: { id: 1, name: t('yes') },
      },
      key: storageKey && `${storageKey}-v1`,
      exclude: ['photos', 'is_active', 'folder'],
    });

  const formik = useFormik<FoodFormValues>({
    initialValues: storedInitialValues,

    validate,

    onSubmit: (values: FoodFormValues) => {
      try {
        onSubmit(values);
        clear();
        formik.resetForm({ values: initialValues });
      } catch (error) {
        toastError(error);
      }
    },
  });

  const params = {
    sort: '',
    perPage: 100,
    field: '',
    page: 1,
    asset_id: formik.values.assets?.id,
  };

  const { data: folders, isLoading: isFoldersLoading } = useQuery<IFolder[]>({
    queryFn: async () => {
      const { data } = await instance.get('admin/asset-folders', {
        params: {
          asset_id: formik.values?.assets?.id,
        },
      });
      return data;
    },
    queryKey: ['asset-folders', formik.values?.assets?.id],
    enabled: !!formik.values?.assets?.id && !!formik.values?.assets?.name,
  });

  const { data: payment, isLoading: isPaymentLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('food-admin-payment-types');
      return data;
    },
    queryKey: ['food-payment-type'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: accommodation, isLoading: isAccommodationLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(`admin/accommodations`, { params });
      return data;
    },
    queryKey: ['accommodations-food', params],
    enabled: !!formik.values.assets?.id,
    onError: error => {
      toastError(error);
    },
  });

  const { data: priceList, isLoading: isPriceListLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(
        `admin/asset-price-lists/${formik.values.assets.id}`
      );
      return data;
    },
    queryKey: ['price-list', formik.values?.assets?.id],
    enabled: !!formik.values.assets?.id,
    onError: error => {
      toastError(error);
    },
  });

  const correctPriceList = priceList?.map((item: string, idx: number) => {
    return { id: idx + 1, name: item };
  });

  const loadCurrentAsset = async (id: number) => {
    if (!id) return;
    try {
      const { data } = await instance.get(`admin/assets/${id}`);
      await formik.setFieldValue('assets.box_office', data?.box_office || []);
    } catch {
      toastError(`${t('failed_to_load')} ${t('assets')}`);
    }
  };

  create(formik.values);

  const setValues = formik.setValues;
  useEffect(() => {
    if (!foodItem) return;
    loadCurrentAsset(foodItem?.assets?.id);

    const foodTypes =
      foodItem?.food_types?.map(item => {
        return {
          id: item.id,
          name: item.name,
          type: item.type,
        };
      }) ?? [];

    setValues({
      name: foodItem.name,
      description: foodItem.description,
      address: foodItem.address,
      is_active: foodItem.is_active
        ? getStatusOptions()[0]
        : getStatusOptions()[1],
      photos: foodItem.photos || [],
      pointObject: foodItem.food_object_type,
      pointType: foodItem.food_object_type?.id,
      food_accommodation: foodItem?.food_accommodation,
      isAllAccomodation: false,
      box_office: foodItem.box_office,
      price_list_code: { id: 0, name: foodItem?.price_list_code ?? '' },
      payment_type: foodItem.payment_types,
      available_kso_order: foodItem.available_kso_order
        ? { id: 1, name: t('yes') }
        : { id: 0, name: t('no') },
      assets: foodItem.assets,
      timezone: getTimezones().find(
        timezone => timezone.id === String(foodItem.timezone)
      ) || { id: '', name: '' },
      containers_enabled: foodItem?.containers_enabled
        ? containerOptions[1]
        : containerOptions[0],
      cooking_time: foodItem?.cooking_time ? String(foodItem.cooking_time) : '',
      folder: foodItem.folder
        ? {
            id: foodItem.folder?.id,
            name: foodItem.folder.name,
            child_folders: foodItem.folder.child_folders || [],
          }
        : { id: 0, name: '', child_folders: [] },
      popup_text: foodItem.popup_text || '',
      popup_is_active: foodItem.popup_is_active,
      limit_hour: foodItem.limit_hour ? foodItem.limit_hour : null,
      enabled_scanner_barcode: foodItem.enabled_scanner_barcode
        ? { id: 1, name: t('yes') }
        : { id: 0, name: t('no') },
      days: setDays(foodItem?.available_day_of_week, days),
      working_days_all: false,
      food_types: foodTypes,
      available_order_today: foodItem.available_order_today
        ? { id: 1, name: t('yes') }
        : { id: 0, name: t('no') },
    });
  }, [foodItem, setValues]);

  const onImagesChange = (image: IPhoto) => {
    const photoExists = formik.values.photos?.find(
      photo => photo.id === image.id
    );

    if (photoExists) {
      formik.setFieldValue(
        'photos',
        formik.values.photos.filter(photo => photo.id !== image.id)
      );
    } else {
      formik.setFieldValue('photos', [...formik.values.photos, image]);
    }
  };

  const handleRemoveImage = (id: number) => {
    formik.setFieldValue(
      'photos',
      formik.values.photos.filter(photo => photo?.id !== id)
    );
  };

  const handleResetImages = () => {
    formik.setFieldValue('photos', []);
  };

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    if (counter) {
      formik.setFieldValue('food_accommodation', []);
    }
  }, [counter]);

  const isEditingBlocked = foodItem && !formik.values.assets;

  const debouncedFoodName = useDebounce(formik.values.name, 500);

  const foodParams = {
    page,
    perPage: 9,
    food_name: debouncedFoodName.trim(),
  };

  return (
    <div className={styles.formWrapper}>
      <form
        className={styles.form}
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <div className={styles.formItems}>
          <CustomSelect
            label={t('food_point_type')}
            value={formik.values.pointObject}
            onChange={value => {
              formik.setFieldValue('pointObject', value);
            }}
            options={pointTypes ? pointTypes?.data : []}
            required
            placeholder={t('select_food_point_type')}
            disabled={isLoading || !!foodItem}
            error={formik.errors.pointObject as string}
          />
          <InputBar
            name="name"
            value={formik.values.name}
            disabled={isLoading}
            label={t('name')}
            star={true}
            placeholder={t('name')}
            onChange={formik.handleChange}
            errors={formik.errors.name}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
          />
          {isAssetEditAvailable && (
            <CustomSelect
              label={t('asset')}
              value={formik.values.assets}
              onChange={value => {
                setCounter(counter + 1);
                formik.setFieldValue('assets', value);
                formik.setFieldValue('box_office', []);
                formik.setFieldValue('price_list_code', { id: 0, name: '' });
                formik.setFieldValue('folder', { id: 0, name: '' });
                formik.setFieldValue('isAllAccomodation', false);
              }}
              options={assets}
              required
              disabled={!isAssetEditAvailable || isLoading}
              error={formik.errors.assets as string}
              placeholder={t('validate_asset')}
            />
          )}
          <CustomSelect
            label={t('cash_register')}
            options={formik?.values?.assets?.box_office || []}
            value={formik?.values?.box_office}
            placeholder={t('cash_register_placeholder')}
            onChange={value => {
              formik.setFieldValue('box_office', value);
            }}
            disabled={formik?.values?.assets?.id === 0}
            required
            error={formik.errors.box_office as string}
          />
          <CustomSelect
            label={t('price_list')}
            options={correctPriceList || []}
            value={formik.values.price_list_code}
            placeholder={t('placeholder_price_lists')}
            onChange={value => {
              formik.setFieldValue('price_list_code', value);
            }}
            disabled={formik?.values?.assets?.id === 0 || isPriceListLoading}
            required
            error={formik.errors.price_list_code as string}
          />
          <div className={styles.limitHourWrapper}>
            <span className={styles.hour_label}>
              {t('available_ordering_time')}{' '}
              <span className={styles.required}>*</span>
            </span>
            <DatePicker.TimePicker
              name="time"
              style={{ boxShadow: 'none' }}
              className={styles.limitHourInput}
              format="HH:mm"
              showNow={false}
              placeholder={t('time_select')}
              onChange={value => {
                formik.setFieldValue(
                  'limit_hour',
                  value ? value.format('HH:mm') : null
                );
              }}
              value={
                formik.values.limit_hour
                  ? dayjs(formik.values.limit_hour, 'HH:mm')
                  : null
              }
              suffixIcon={null}
              minuteStep={5}
            />
            {formik.errors.limit_hour && (
              <span className={styles.errorMsg}>
                {formik.errors.limit_hour as string}
              </span>
            )}
          </div>
          <FolderSelect
            label={t('folder_with_menu')}
            folders={folders || []}
            assetId={formik?.values?.assets?.id}
            folderId={4}
            onChange={value => {
              formik.setFieldValue('folder', value);
            }}
            disabled={
              !folders || isFoldersLoading || !formik?.values?.assets?.name
            }
          />
          <div className={styles.paymentWrapper}>
            <CustomSelect
              label={t('payment_type')}
              options={payment}
              value={formik?.values?.payment_type}
              placeholder={t('payment_placeholder')}
              onChange={value => {
                formik.setFieldValue('payment_type', value);
              }}
              disabled={!payment?.length || isPaymentLoading}
              required
              multiselect
              error={formik.errors.payment_type as string}
            />
            {(formik.values.pointObject.id === 2 ||
              formik.values.pointObject.id === 1) && (
              <CustomSelect
                label={t('available_kso_order')}
                placeholder={t('available_kso_order_placeholder')}
                value={formik?.values.available_kso_order}
                options={[
                  { id: 0, name: t('no') },
                  { id: 1, name: t('yes') },
                ]}
                onChange={value => {
                  formik.setFieldValue('available_kso_order', value);
                }}
              />
            )}
          </div>
          <CustomSelect
            label={t('issue_in_containers')}
            placeholder={t('choose')}
            value={formik?.values.containers_enabled}
            options={containerOptions}
            onChange={value =>
              formik.setFieldValue('containers_enabled', value)
            }
          />
          <div className={styles.addressWrapper}>
            <InputBar
              name="address"
              value={formik.values.address}
              disabled={isLoading}
              label={t('food_address_ourlet')}
              star={true}
              placeholder={t('food_address_ourlet')}
              onChange={formik.handleChange}
              errors={formik.errors.address}
              onBlur={formik.handleBlur}
              error={formik.touched.address && Boolean(formik.errors.address)}
            />
          </div>
          {formik.values.pointObject.id === 2 && (
            <InputBar
              name="cooking_time"
              label={t('cooking_time')}
              value={formik.values.cooking_time}
              placeholder={t('cooking_time_placeholder')}
              onChange={formik.handleChange}
              errors={formik.errors.cooking_time}
              onBlur={formik.handleBlur}
              error={
                formik.touched.cooking_time &&
                Boolean(formik.errors.cooking_time)
              }
            />
          )}
          <Textarea
            showCounter
            rows={4}
            label={t('description')}
            placeholder={t('description')}
            maxLength={1000}
            value={formik.values.description}
            onChange={formik.handleChange}
            name="description"
            star={true}
            disabled={isLoading}
            errors={formik.errors.description}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
          />
          <div className={styles.warningWrapper}>
            <Textarea
              showCounter
              rows={4}
              label={t('warning_description')}
              placeholder={t('add_warning_description')}
              maxLength={1000}
              value={formik.values.popup_text}
              onChange={formik.handleChange}
              name="popup_text"
              star={false}
              disabled={isLoading}
              errors={formik.errors.popup_text}
              error={Boolean(formik.errors.popup_text)}
            />
            <label className={styles.checkboxWrapper}>
              <Checkbox
                checked={formik?.values?.popup_is_active}
                onChange={event => {
                  formik.setFieldValue('popup_is_active', event.target.checked);
                }}
              />
              <span className={styles.checkboxLabel}>
                {t('warning_active')}
              </span>
            </label>
          </div>
          <div className={styles.field}>
            <CustomSelect
              label={t('status')}
              value={formik.values.is_active}
              onChange={value => {
                formik.setFieldValue('is_active', value);
              }}
              options={getStatusOptions()}
              placeholder={t('select_status')}
              disabled={isLoading}
            />
          </div>

          <CustomSelect
            label={t('food_category')}
            placeholder={t('select_food_categories')}
            value={formik.values.food_types}
            options={foodCategory}
            disabled={isFoodTypeLoading}
            required
            multiselect
            onChange={value => {
              formik.setFieldValue('food_types', value);
            }}
            onTouch={() => formik.setFieldTouched('food_types', true)}
            error={
              formik.touched.food_types
                ? (formik.errors.food_types as string)
                : undefined
            }
          />

          <CustomSelect
            label={t('schedule')}
            options={days}
            value={formik.values.days}
            onChange={value => formik.setFieldValue('days', value)}
            multiselect
            placeholder={t('select_days')}
            required
            onTouch={() => formik.setFieldTouched('days', true)}
            error={
              formik.touched.days ? (formik.errors.days as string) : undefined
            }
            disabled={formik.values.working_days_all}
            hasCheckbox
            checkboxLabel={t('on_all_days')}
            setIsCheckedAll={value =>
              formik.setFieldValue('working_days_all', value)
            }
          />
          <CustomSelect
            label={t('order_today')}
            value={formik.values.available_order_today}
            placeholder={t('order_availability_placeholder')}
            options={[
              { id: 0, name: t('no') },
              { id: 1, name: t('yes') },
            ]}
            onChange={value =>
              formik.setFieldValue('available_order_today', value)
            }
          />

          <CustomSelect
            label={t('time_zone')}
            options={timezonesList}
            value={formik.values.timezone || ''}
            placeholder={t('timezone_validate')}
            onChange={value => {
              formik.setFieldValue('timezone', value);
            }}
            required
            error={formik.errors.timezone as string}
          />
          <div className={styles.accommodationWrapper}>
            <CustomSelect
              label={t('accommodation_facility')}
              options={accommodation?.data}
              value={formik.values.food_accommodation}
              placeholder={t('to_choose')}
              onChange={value => {
                formik.setFieldValue('food_accommodation', value);
              }}
              isSearchEnabled
              disabled={
                isEditingBlocked ||
                (isHaveNotEnoughRole && isAssetAdmin) ||
                isLoading ||
                formik.values.isAllAccomodation ||
                isAccommodationLoading ||
                !accommodation
              }
              required
              multiselect
              error={formik.errors.food_accommodation as string}
              hasCheckbox
              checkboxLabel={t('all_accommodation_selection')}
              setIsCheckedAll={value =>
                formik.setFieldValue('isAllAccomodation', value)
              }
            />
          </div>

          <CustomSelect
            label={t('show_qr')}
            placeholder={t('show_qr')}
            value={formik?.values?.enabled_scanner_barcode}
            onChange={value =>
              formik.setFieldValue('enabled_scanner_barcode', value)
            }
            options={barcodeOptions}
          />

          <div className={styles.imagesContainer}>
            <ul className={styles.imagesList}>
              {formik?.values?.photos?.map(image => (
                <div key={image.id} className={styles.imageWrapper}>
                  <img
                    className={styles.image}
                    src={image.full_photo_url}
                    alt="Announcement"
                  />
                  {hasAvailable && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(image.id)}
                      className={styles.deleteImageButton}
                    >
                      <CloseIcon className={styles.closeIcon} />
                    </button>
                  )}
                </div>
              ))}
            </ul>

            {hasAvailable && (
              <>
                <IconButton
                  onClick={() => setIsModalOpen(true)}
                  className={styles.pickPhotoButton}
                  isDisabled={!formik.values.name}
                >
                  <PhotoIcon />
                  <span className={styles.pickPhotoText}>
                    {t('choose_photo_for_food')}
                  </span>
                </IconButton>
                {formik.errors.photos && (
                  <span className={styles.errorMsg}>
                    {formik.errors.photos as string}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <Button
          type="submit"
          text={t('save_n_exit')}
          className={styles.formBtn}
          disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}
        />

        <Modal
          modalStyles={styles.photosModal}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <PhotoPickerContainer
            queryKey={['food-photos', foodParams]}
            queryFn={() => getFoodPhotos(foodParams)}
            enabled={foodParams.food_name !== ''}
            initialPhotos={foodItem ? foodItem.photos : []}
            selectedImages={formik.values.photos || []}
            onImageChange={onImagesChange}
            onCloseClick={() => setIsModalOpen(false)}
            onResetClick={handleResetImages}
            changePage={setPage}
          />
        </Modal>
      </form>
    </div>
  );
};
